import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { styled, alpha } from '@mui/material/styles';
import { parseError } from '@lov/shared';
import { Button, Input, Alert } from '../ui';
import { useForgotPassword } from '../../hooks/auth';

const forgotPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

const SuccessCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  padding: theme.spacing(2),
}));

const SuccessIcon = styled(MarkEmailReadIcon)(({ theme }) => ({
  fontSize: 64,
  color: theme.palette.success.main,
  marginBottom: theme.spacing(2),
  filter: `drop-shadow(0 0 20px ${alpha(theme.palette.success.main, 0.4)})`,
}));

export function ForgotPasswordForm() {
  const { mutate: forgotPassword, isPending, isError, error, isSuccess } = useForgotPassword();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = (data: ForgotPasswordFormData) => {
    forgotPassword(data);
  };

  if (isSuccess) {
    return (
      <SuccessCard>
        <SuccessIcon />
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Check Your Email
        </Typography>
        <Typography variant="body2" color="text.secondary">
          We've sent password reset instructions to your email address. Please check your inbox.
        </Typography>
      </SuccessCard>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {isError && (
        <Alert variant="error">
          {parseError(error).message}
        </Alert>
      )}

      <Input
        label="Email"
        type="email"
        {...register('email')}
        error={errors.email?.message}
        disabled={isPending}
        autoComplete="email"
        helperText="Enter the email associated with your account"
        fullWidth
      />

      <Button type="submit" fullWidth isLoading={isPending} glow>
        Send Reset Link
      </Button>
    </Box>
  );
}
